<template>
  <div>
    <v-row class="pt-3 justify-center">
      <SelectorButton
        :title="title"
        type="travelTracker"
        class=""
        @click="handleChangeTravelTrackerEnabled"
      ></SelectorButton>
    </v-row>
    <v-row>
      <v-col cols="12" class="pb-0">
        <h4>Date</h4>
        <div>
          <TravelTrackerDateSelector
            :selected="selectedDate"
            @change="handleChangeDate"
          ></TravelTrackerDateSelector>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0 pt-0">
        <h4>Time: {{ new Date().toString().match("GMT.*")[0] }}</h4>
        <div>
          <TravelTrackerTimeSelector
            :selected="selectedTime"
            @change="handleChangeTime"
          ></TravelTrackerTimeSelector>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <h4>Traveler Type</h4>
        <div>
          <TravelTrackerTravelerTypeSelector
            :selected="selectedTravelerTypes"
            @change="handleChangeTravelerType"
          ></TravelTrackerTravelerTypeSelector>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <h4>Travel Type</h4>
        <div>
          <TravelTrackerTravelTypeSelector
            :selected="selectedTravelTypes"
            @change="handleChangeTravelType"
          ></TravelTrackerTravelTypeSelector>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0">
        <h4>Is VIP?</h4>
        <div>
          <TravelTrackerVipSelector
            :selected="selectedIsVip"
            @change="handleChangeIsVip"
          ></TravelTrackerVipSelector>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
} from "@/store";
import {
  getPreference,
  savePreference,
} from "@/services/isosTravelTracker/travelTracker.service";
import dayjs from "dayjs";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

import lodashIsEqual from "lodash/isEqual";
import { SAVE_PREFERENCES } from "@/store/actions.type";

const DEFAULT_DATE = dayjs();
const DEFAULT_TIME = dayjs().hour();
const DEFAULT_SECURITY_RISK = [0, 9];
const DEFAULT_TRAVELER_TYPES = ["international", "domestic"];

const EMITTED_ACTIONS = {
  CHANGE_TRAVEL_TRACKER_ENABLED: "change-travel-tracker-enabled",
  CHANGE_TRIP_STATUSES: "change-trip-statuses",
  CHANGE_IS_VIP: "change-is-vip",
  CHANGE_TRAVELER_TYPE: "change-traveler-type",
  CHANGE_TRAVEL_TYPE: "change-travel-type",
  CHANGE_LOCATION: "change-location",
  CHANGE_SECURITY_RISK: "change-security-risk",
  CHANGE_TIME: "change-time",
  CHANGE_DATE: "change-date",
  CHANGE_HAS_FILTERS_SET: "change-has-filters-set",
  CHANGE_SHOULD_PULL_DATA: "change-should-pull-data",
  RESET: "reset",
};

export default {
  name: "TravelTrackerContentFilters",
  components: {
    TravelTrackerVipSelector: () =>
      import("@/components/isosTravelTracker/TravelTrackerVipSelector"),
    TravelTrackerTravelerTypeSelector: () =>
      import(
        "@/components/isosTravelTracker/TravelTrackerTravelerTypeSelector"
      ),
    TravelTrackerTravelTypeSelector: () =>
      import("@/components/isosTravelTracker/TravelTrackerTravelTypeSelector"),
    TravelTrackerDateSelector: () =>
      import("@/components/isosTravelTracker/TravelTrackerDateSelector"),
    TravelTrackerTimeSelector: () =>
      import("@/components/isosTravelTracker/TravelTrackerTimeSelector"),
    SelectorButton: () => import("@/components/common/SelectorButton"),
  },
  props: {
    resetTimestamp: {
      type: String,
      default: new Date().toISOString(),
    },
  },
  data() {
    return {
      travelTrackerEnabled: false,
      selectedTripStatuses: [],
      selectedIsVip: [],
      selectedTravelerTypes: [],
      selectedTravelTypes: [],
      selectedLocations: [],
      selectedSecurityRisks: JSON.parse(JSON.stringify(DEFAULT_SECURITY_RISK)),
      selectedTime: JSON.parse(JSON.stringify(DEFAULT_TIME)),
      selectedDate: DEFAULT_DATE,
    };
  },
  mounted() {
    this.getPrefs();
  },
  computed: {
    ...mapGetters({
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      resetMapIcons: MODULE_PREFIX_MAP + "resetMapIcons",
      userPreferences: MODULE_PREFIX_USER + "preferences",
    }),
    title() {
      return this.travelTrackerEnabled ? "Enabled" : "Disabled";
    },
  },
  methods: {
    handleChangeTravelTrackerEnabled() {
      this.travelTrackerEnabled = !this.travelTrackerEnabled;
      this.selectorPanel["travelTracker"] = this.travelTrackerEnabled;
      savePreference(
        this.$store,
        this.travelTrackerEnabled,
        "enabled",
        "travelTracker"
      );
      this.$emit(
        EMITTED_ACTIONS.CHANGE_TRAVEL_TRACKER_ENABLED,
        this.travelTrackerEnabled
      );
      if (this.travelTrackerEnabled) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.clicked,
          "Travel Tracker Enabled - " + this.travelTrackerEnabled
        );
      }
    },
    handleChangeTripStatuses(items) {
      this.selectedTripStatuses = items;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_TRIP_STATUSES,
        this.selectedTripStatuses
      );
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.isos,
        EVENT_ACTIONS.clicked,
        "Trip Status - " + this.selectedTripStatuses
      );
    },
    handleChangeIsVip(items) {
      this.selectedIsVip = items;
      this.$emit(EMITTED_ACTIONS.CHANGE_IS_VIP, this.selectedIsVip);
      if (items.length > 0) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.clicked,
          "Is Vip? - " + this.selectedIsVip
        );
      }
    },
    handleChangeTravelerType(items) {
      this.selectedTravelerTypes = items;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_TRAVELER_TYPE,
        this.selectedTravelerTypes
      );
      if (!lodashIsEqual(DEFAULT_TRAVELER_TYPES, items)) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.clicked,
          "Traveler Type - " + this.selectedTravelerTypes
        );
      }
    },
    handleChangeTravelType(items) {
      this.selectedTravelTypes = items;
      this.$emit(EMITTED_ACTIONS.CHANGE_TRAVEL_TYPE, this.selectedTravelTypes);
      if (items.length > 0) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.clicked,
          "Travel Type - " + this.selectedTravelTypes
        );
      }
    },
    handleChangeLocation(items) {
      this.selectedLocations = items;
      this.$emit(EMITTED_ACTIONS.CHANGE_LOCATION, this.selectedLocations);
    },
    handleChangeSecurityRisk(items) {
      this.selectedSecurityRisks = items;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_SECURITY_RISK,
        this.selectedSecurityRisks
      );
    },
    handleChangeDate(items) {
      this.selectedDate = items;
      this.$emit(EMITTED_ACTIONS.CHANGE_DATE, this.selectedDate);
      if (DEFAULT_DATE.$D !== items.$D) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.changed,
          "Date - Day Offset- " + this.selectedDate.diff(DEFAULT_DATE, "day")
        );
      }
      if (DEFAULT_DATE.$W !== items.$W) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.changed,
          "Date - Day of the Week- " + this.selectedDate.$W
        );
      }
    },
    handleChangeTime(items) {
      this.selectedTime = items;
      this.$emit(EMITTED_ACTIONS.CHANGE_TIME, this.selectedTime);
      if (items !== DEFAULT_TIME) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.isos,
          EVENT_ACTIONS.changed,
          "Time - " + this.selectedTime
        );
      }
    },
    async reset() {
      this.travelTrackerEnabled = false;
      this.selectedTripStatus = [];
      this.selectedIsVip = [];
      this.selectedTravelerTypes = [];
      this.selectedTravelTypes = [];
      this.selectedLocations = [];
      this.selectedTime = JSON.parse(JSON.stringify(DEFAULT_TIME));
      this.selectedDate = DEFAULT_DATE;
      this.selectedSecurityRisks = JSON.parse(
        JSON.stringify(DEFAULT_SECURITY_RISK)
      );
    },
    getPrefs() {
      // Get saved User Preferences for if Travel Tracker is enabled
      this.travelTrackerEnabled =
        getPreference(this.$store, true, "enabled", "travelTracker") ??
        this.travelTrackerEnabled;
      this.selectorPanel["travelTracker"] = this.travelTrackerEnabled;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_TRAVEL_TRACKER_ENABLED,
        this.travelTrackerEnabled
      );
    },
    async clearAllDataFromMap() {
      this.travelTrackerEnabled = false;
      this.selectorPanel["travelTracker"] = this.travelTrackerEnabled;
      savePreference(
        this.$store,
        this.travelTrackerEnabled,
        "enabled",
        "travelTracker"
      );
      this.$emit(
        EMITTED_ACTIONS.CHANGE_TRAVEL_TRACKER_ENABLED,
        this.travelTrackerEnabled
      );
    },
  },
  watch: {
    resetTimestamp: {
      handler() {
        this.reset();
      },
    },
    resetMapIcons: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          this.clearAllDataFromMap();
        }
      },
    },
  },
};
</script>

<style scoped></style>
